/**
 * Caption segments: subtitle blocks timed on the CLIP timeline, independent of slides.
 * Shared by the renderer normalizers, the Remotion compositions and the (vendored)
 * frontend editor. Must stay free of Node imports.
 *
 * When a clip has at least one valid segment they are the ONLY subtitle source —
 * per-slide `text` and the clip-level `subtitleText` are suppressed.
 */
import {DEFAULT_FPS} from './timeline';

export interface CaptionSegment {
  id: string;
  text: string;
  /** Seconds on the clip timeline. */
  start: number;
  /** Seconds on the clip timeline, strictly greater than `start`. */
  end: number;
  /** Canvas fractions 0..1 anchored at the block center. Absent = legacy anchor. */
  posX?: number;
  posY?: number;
  scale?: number;
}

const finiteNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const optionalFraction = (value: unknown): number | undefined => {
  const numeric = finiteNumber(value);
  if (numeric === undefined || numeric < 0) {
    return undefined;
  }
  return Math.min(1, numeric);
};

const optionalScale = (value: unknown): number | undefined => {
  const numeric = finiteNumber(value);
  return numeric !== undefined && numeric > 0 ? numeric : undefined;
};

/**
 * Reads the raw `caption_segments` metadata value (array or JSON string) into typed
 * segments. Invalid entries (missing text, unusable or inverted times) are dropped
 * rather than throwing, and the survivors come back sorted by `start`.
 */
export const parseCaptionSegments = (raw: unknown): CaptionSegment[] => {
  const list = toArray(raw);
  const segments: CaptionSegment[] = [];

  list.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const text = typeof record.text === 'string' ? record.text : String(record.text ?? '');
    const start = finiteNumber(record.start);
    const end = finiteNumber(record.end);
    if (!text.trim() || start === undefined || end === undefined || start < 0 || end <= start) {
      return;
    }

    const posX = optionalFraction(record.pos_x ?? record.posX);
    const posY = optionalFraction(record.pos_y ?? record.posY);
    const scale = optionalScale(record.scale);
    const id = typeof record.id === 'string' && record.id.trim() ? record.id : `segment-${index + 1}`;

    segments.push({
      id,
      text,
      start,
      end,
      ...(posX === undefined ? {} : {posX}),
      ...(posY === undefined ? {} : {posY}),
      ...(scale === undefined ? {} : {scale}),
    });
  });

  return segments.sort((left, right) => left.start - right.start);
};

const toArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * The editor enforces non-overlapping segments; a payload that overlaps anyway must not
 * stack two overlays. Every boundary splits the timeline into slices, each slice shows the
 * segment that wins there (`activeSegmentAt`, last writer), and adjacent slices belonging
 * to the same segment merge back together. A segment can therefore come back after being
 * interrupted, and one that is never on top disappears.
 */
export const nonOverlappingSegments = (segments: CaptionSegment[]): CaptionSegment[] => {
  if (segments.length < 2) {
    return segments.filter((segment) => segment.end > segment.start);
  }

  const boundaries = Array.from(new Set(segments.flatMap((segment) => [segment.start, segment.end]))).sort(
    (left, right) => left - right,
  );

  const slices: CaptionSegment[] = [];
  for (let index = 0; index + 1 < boundaries.length; index += 1) {
    const start = boundaries[index];
    const end = boundaries[index + 1];
    const winner = activeSegmentAt(segments, start);
    if (!winner) {
      continue;
    }

    const previous = slices[slices.length - 1];
    if (previous && previous.id === winner.id && previous.end === start) {
      previous.end = end;
      continue;
    }
    slices.push({...winner, start, end});
  }

  return slices;
};

export interface SubtitlePlan {
  segments: CaptionSegment[];
  /** The clip-level `subtitleText` overlay spanning the whole clip. */
  showClipSubtitle: boolean;
  /** Per-slide `text` overlays inside each slide sequence. */
  showSlideText: boolean;
}

/**
 * Which subtitle source a clip renders. Hidden subtitles silence all three. Otherwise
 * segments win outright: when at least one exists both legacy sources are suppressed, and
 * failing that the clip-level text wins over slide text, exactly as before the narration wave.
 */
export const subtitlePlan = (
  segments: CaptionSegment[] | undefined,
  clipSubtitleText: string,
  subtitlesHidden = false,
): SubtitlePlan => {
  // `show_subtitles=false` silences every source. Gating only the segments would let a
  // clip whose captions were converted to segments fall back to the stale per-slide text.
  if (subtitlesHidden) {
    return {segments: [], showClipSubtitle: false, showSlideText: false};
  }

  const list = segments ?? [];
  if (list.length > 0) {
    return {segments: list, showClipSubtitle: false, showSlideText: false};
  }

  const hasClipSubtitle = Boolean(clipSubtitleText.trim());
  return {segments: [], showClipSubtitle: hasClipSubtitle, showSlideText: !hasClipSubtitle};
};

export interface CaptionWindow {
  segment: CaptionSegment;
  fromFrame: number;
  durationInFrames: number;
}

/**
 * Segment windows as Remotion `<Sequence>` bounds. Each window is at least one frame
 * long, so a sub-frame segment still shows rather than silently disappearing.
 */
export const captionWindows = (segments: CaptionSegment[], fps: number): CaptionWindow[] => {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;

  return nonOverlappingSegments(segments).map((segment) => {
    const fromFrame = Math.max(0, Math.round(segment.start * safeFps));
    const endFrame = Math.round(segment.end * safeFps);
    return {segment, fromFrame, durationInFrames: Math.max(1, endFrame - fromFrame)};
  });
};

/** The segment visible at `timeSec` on the clip timeline; the last match wins on overlap. */
export const activeSegmentAt = (
  segments: CaptionSegment[],
  timeSec: number,
): CaptionSegment | undefined => {
  let active: CaptionSegment | undefined;
  for (const segment of segments) {
    if (timeSec >= segment.start && timeSec < segment.end) {
      active = segment;
    }
  }
  return active;
};
